'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  checkReleaseUpdate,
  normalizeSemverTag,
  shouldPulseUpdate,
} from '@/lib/update-check'
import { APP_VERSION, RELEASE_FEED_URL } from '@/lib/app-meta'

const ONE_HOUR_IN_MS = 60 * 60 * 1000
const MUTED_UPDATE_VERSION_KEY = 'aimanju:update:muted-version'
const RELEASE_UPDATE_SESSION_CACHE_KEY = 'aimanju:update:session-cache:v1'

export interface ReleaseUpdateInfo {
  latestVersion: string
  releaseUrl: string
  releaseName: string | null
  publishedAt: string | null
}

export interface UseReleaseUpdateResult {
  currentVersion: string
  update: ReleaseUpdateInfo | null
  shouldPulse: boolean
  showModal: boolean
  isChecking: boolean
  checkError: string | null
  openModal: () => void
  dismissCurrentUpdate: () => void
  checkNow: () => Promise<void>
}

type ReleaseUpdateSessionCache = {
  currentVersion: string
  checkedAt: number
  update: ReleaseUpdateInfo | null
  checkError: string | null
}

function readMutedUpdateVersion(): string | null {
  if (typeof window === 'undefined') return null
  return window.localStorage.getItem(MUTED_UPDATE_VERSION_KEY)
}

function writeMutedUpdateVersion(version: string): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(MUTED_UPDATE_VERSION_KEY, version)
}

function readSessionCache(): ReleaseUpdateSessionCache | null {
  if (typeof window === 'undefined') return null
  const raw = window.sessionStorage.getItem(RELEASE_UPDATE_SESSION_CACHE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as ReleaseUpdateSessionCache
    if (!parsed || typeof parsed !== 'object') return null
    if (typeof parsed.currentVersion !== 'string') return null
    if (typeof parsed.checkedAt !== 'number') return null
    return parsed
  } catch {
    return null
  }
}

function writeSessionCache(payload: ReleaseUpdateSessionCache): void {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(RELEASE_UPDATE_SESSION_CACHE_KEY, JSON.stringify(payload))
}

export function useReleaseUpdate(): UseReleaseUpdateResult {
  const currentVersion = useMemo(() => normalizeSemverTag(APP_VERSION), [])
  const shouldCheckRelease = process.env.NODE_ENV === 'production' && RELEASE_FEED_URL.length > 0

  const [update, setUpdate] = useState<ReleaseUpdateInfo | null>(null)
  const [shouldPulse, setShouldPulse] = useState(false)
  const [checkError, setCheckError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const latestRequestRef = useRef(0)

  const applyUpdate = useCallback((nextUpdate: ReleaseUpdateInfo | null, nextError: string | null) => {
    setCheckError(nextError)
    setUpdate(nextUpdate)
    setShowModal(false)
    const mutedVersion = readMutedUpdateVersion()
    setShouldPulse(nextUpdate ? shouldPulseUpdate(nextUpdate.latestVersion, mutedVersion) : false)
  }, [])

  const runCheck = useCallback(async (forceRefresh: boolean) => {
    if (!shouldCheckRelease) {
      applyUpdate(null, null)
      setIsChecking(false)
      return
    }

    const requestId = latestRequestRef.current + 1
    latestRequestRef.current = requestId
    setIsChecking(true)

    if (!forceRefresh) {
      const cached = readSessionCache()
      if (
        cached &&
        cached.currentVersion === currentVersion &&
        Date.now() - cached.checkedAt < ONE_HOUR_IN_MS
      ) {
        applyUpdate(cached.update, cached.checkError)
        setIsChecking(false)
        return
      }
    }

    const result = await checkReleaseUpdate({
      feedUrl: RELEASE_FEED_URL,
      currentVersion,
    })
    if (requestId !== latestRequestRef.current) return

    if (result.kind === 'error') {
      applyUpdate(null, result.message)
      writeSessionCache({
        currentVersion,
        checkedAt: Date.now(),
        update: null,
        checkError: result.message,
      })
      setIsChecking(false)
      return
    }

    if (result.kind === 'no-release') {
      applyUpdate(null, null)
      writeSessionCache({
        currentVersion,
        checkedAt: Date.now(),
        update: null,
        checkError: null,
      })
      setIsChecking(false)
      return
    }

    if (result.kind === 'no-update') {
      applyUpdate(null, null)
      writeSessionCache({
        currentVersion,
        checkedAt: Date.now(),
        update: null,
        checkError: null,
      })
      setIsChecking(false)
      return
    }

    const nextUpdate: ReleaseUpdateInfo = {
      latestVersion: result.latestVersion,
      releaseUrl: result.release.htmlUrl,
      releaseName: result.release.name,
      publishedAt: result.release.publishedAt,
    }

    applyUpdate(nextUpdate, null)
    writeSessionCache({
      currentVersion,
      checkedAt: Date.now(),
      update: nextUpdate,
      checkError: null,
    })
    setIsChecking(false)
  }, [applyUpdate, currentVersion, shouldCheckRelease])

  const checkNow = useCallback(async () => {
    await runCheck(true)
  }, [runCheck])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      if (cancelled) return
      if (typeof document !== 'undefined' && document.hidden) return
      await runCheck(false)
    }

    void run()
    document.addEventListener('visibilitychange', run)
    const timer = window.setInterval(() => {
      void run()
    }, ONE_HOUR_IN_MS)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', run)
      window.clearInterval(timer)
    }
  }, [runCheck])

  const dismissCurrentUpdate = useCallback(() => {
    if (update) {
      writeMutedUpdateVersion(update.latestVersion)
    }
    setShouldPulse(false)
    setShowModal(false)
  }, [update])

  const openModal = useCallback(() => {
    if (!update) return
    setShowModal(true)
  }, [update])

  return {
    currentVersion,
    update,
    shouldPulse,
    showModal,
    isChecking,
    checkError,
    openModal,
    dismissCurrentUpdate,
    checkNow,
  }
}
